import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import Label from '@/Controls/Label';
import { IIteratorDTO } from "coreframetypings/dto";
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import IAsyncFactory from '@/Factories/IAsyncFactory';

export default class AssociationMultiLabelFactory extends ControlFactory implements IAsyncFactory {

    /**
     *  @override
     */
    control: Label;
    private jQueryControl: JQuery;
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
    protected Label = ComponentManager.getControl<typeof Label>('Label');

    private defaultIterator: IIteratorDTO;
    private iterator: IIteratorDTO;

    /**
     * @override
     */
    public viewModel: LabelViewModel;

    /**
     * @override
     */
    public context: DetailViewContext;

    /**
     * Gibt an, ob die Factory komplett geladen ist.
     */
    public isFullyLoaded: KnockoutObservable<boolean>;

    public constructor(params: any) {
        super(params);

        this.defaultIterator = this.context.defaultIterators[this.detailViewItem.name];
        this.isFullyLoaded = this.observableOrDefault(false);
    }

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = new this.LabelViewModel({
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            invalidationText: this.invalidationText,
            isValid: this.isValid,
            placeholder: '-',
            parentLocator: this.locator,
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            locator: this.detailViewItem.name,
            caption: this.defineAsyncComputed(() => {
                this.deleteIterators([this.iterator]);
                let success = false;

                return $.when(this.getIterator()).then((iterator) => {
                    this.iterator = iterator;
                    const extentCount = parseInt(iterator.extentCount);
                    if (extentCount > 0) {
                        return RADEnvironmentProxy.getInstance().getExtentWithPDOIDAsFirstColumn({
                            iteratorID: iterator.id,
                            start: 0,
                            end: extentCount - 1
                        });
                    }
                    return null;
                }).then((arrayOfGeneric) => {
                    if (arrayOfGeneric != null) {
                        let values = [];

                        for (let i = 0; i < arrayOfGeneric.length; i++) {
                            values.push(arrayOfGeneric[i][1]);
                        }

                        return values.join('; ');
                    }
                    return '-';
                }).then(x => {
                    success = true;
                    return x;
                }).always(() => {
                    if (this.isDisposed)
                        return;
                    
                    if (success)
                        this.isFullyLoaded(true);
                });
            }, '-' as any) 
        });

        this.control = new this.Label(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        return this.jQueryControl;
    }

    private getIterator(): JQueryPromise<IIteratorDTO> {
        return RADEnvironmentProxy.getInstance().createIteratorForAssoziation({
            pdTypeName: this.context.genericObject.pdTypeName,
            pdo_id: this.context.genericObject.pdoId,
            assoziationName: this.detailViewItem.name,
            properties: ['ObjectIdentValue'],
            retrieveExtentCount: true,
            itOperator: {
                includeAllPrincipals: this.detailViewItem.listView.includeAllPrincipals,
                includeBasePrincipals: this.detailViewItem.listView.includeBasePrincipals,
                includeSubPrincipals: this.detailViewItem.listView.includeSubPrincipals,
                memoryOptimizationEnabled: true,
                onlySubPrincipals: null,
                historizationDates: null,
                isGridIterator: null,
                principalFilter: null,
                searchObjectPositionRequest: null,
                visibleDeletedObjects: null,
                hideConnectedElementsRequest: null
            },
            filter: '',
            orderby: ''
        }).promise();
    }

    private deleteIterators(iterators: IIteratorDTO[]): void {
        if (this.isDisposed || iterators === null) {
            return;
        }

        iterators = iterators.filter(function (it) { return it != null; });

        if (iterators !== null && iterators.length > 0) {
            RADEnvironmentProxy.getInstance().deleteIterators({
                iteratorIDs: iterators.map(function (it) { return parseInt(it.id); })
            });
        }
    }

    protected disposeInternal(): void {
        this.deleteIterators([this.defaultIterator, this.iterator]);
        super.disposeInternal();
    }
}

ComponentManager.setFactory('AssociationMultiLabel', AssociationMultiLabelFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');
ComponentManager.setFactory('AssoziationMultiLabel', AssociationMultiLabelFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');